import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Input,
  Select,
  message,
} from "antd";
import { platformApi } from "../../../../api";
import ServicesTable from "../../VehicleMaster/VehicleModal/ServicesTable";

const { Item } = Form;
const { Option } = Select;

const ServiceScheduleModal = (props) => {
  const [loading, setLoading] = useState(false);
  const [editable, setEditable] = useState(props.editable || false);
  const [services, setServices] = useState([]);
  const [serviceCount, setServiceCount] = useState(0);
  const [modelCodeOptions, setModelCodeOptions] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);

  const { form, open, close, data, modify } = props;
  const { getFieldDecorator } = form;

  useEffect(() => {
    setEditable(props.editable || false);
  }, [props.editable, open]);

  // Fetch model codes when modal opens (unlinked vehicles + currently assigned vehicles for this schedule)
  useEffect(() => {
    if (open) {
      const scheduleIdParam = data && data.id ? `?scheduleId=${data.id}` : "";
      platformApi
        .get(`/api/serviceScheduleMaster/modelCodes${scheduleIdParam}`)
        .then((res) => {
          if (res.data && res.data.code === 200 && res.data.response?.data) {
            setModelCodeOptions(res.data.response.data);
          }
        })
        .catch((err) => console.error("Error fetching model codes:", err));
    }
  }, [open, data]);

  useEffect(() => {
    if (data && open) {
      const initialCodes =
        Array.isArray(data.modelCodes) && data.modelCodes.length > 0
          ? data.modelCodes
          : data.baseModel
          ? data.baseModel.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

      setSelectedCodes(initialCodes);
      form.setFieldsValue({
        modelCodes: initialCodes,
        warrantyDays: data.warrantyDays,
        warrantyKm: data.warrantyKm,
        noOfServices: data.noOfServices,
        serviceIntervalDays: data.serviceIntervalDays,
        serviceIntervalKm: data.serviceIntervalKm,
      });

      const initialServices = (data.services || []).map((s, idx) => ({
        id: s.id || `temp-${idx}`,
        serviceNo: s.serviceNo || idx + 1,
        serviceType: s.serviceType || "FREE",
        serviceDays: s.serviceDays || 0,
        serviceKm: s.serviceKm || 0,
      }));
      setServices(initialServices);
      setServiceCount(data.noOfServices || initialServices.length);
    } else if (!data && open) {
      setSelectedCodes([]);
      form.resetFields();
      setServices([]);
      setServiceCount(0);
    }
  }, [data, open]);

  const handleServiceCountChange = (e) => {
    const count = parseInt(e.target.value, 10) || 0;
    setServiceCount(count);
  };

  const handleModelCodesChange = (values) => {
    setSelectedCodes(values || []);
  };

  const handleSubmit = () => {
    form.validateFields((err, values) => {
      if (!err) {
        setLoading(true);
        const codes = values.modelCodes || [];
        const payload = {
          modelCodes: codes,
          baseModel: codes.join(", "),
          warrantyDays: values.warrantyDays ? parseInt(values.warrantyDays, 10) : null,
          warrantyKm: values.warrantyKm ? parseInt(values.warrantyKm, 10) : null,
          noOfServices: values.noOfServices ? parseInt(values.noOfServices, 10) : null,
          serviceIntervalDays: values.serviceIntervalDays ? parseInt(values.serviceIntervalDays, 10) : null,
          serviceIntervalKm: values.serviceIntervalKm ? parseInt(values.serviceIntervalKm, 10) : null,
          services: (services || []).map((s) => ({
            serviceNo: s.serviceNo ? parseInt(s.serviceNo, 10) : null,
            serviceType: s.serviceType,
            serviceDays: s.serviceDays ? parseInt(s.serviceDays, 10) : 0,
            serviceKm: s.serviceKm ? parseInt(s.serviceKm, 10) : 0,
          })),
        };

        const apiCall = data && data.id
          ? platformApi.put(`/api/serviceScheduleMaster/${data.id}`, payload)
          : platformApi.post("/api/serviceScheduleMaster", payload);

        apiCall
          .then((res) => {
            setLoading(false);
            if (res.data && res.data.code === 200) {
              message.success(data ? "Service Schedule Master updated!" : "Service Schedule Master created!");
              close();
              props.emitData();
            } else {
              message.error(res.data.msg || "Failed to save");
            }
          })
          .catch((error) => {
            setLoading(false);
            console.error(error);
            message.error(error.message || "An error occurred");
          });
      }
    });
  };

  const codeLabel = selectedCodes && selectedCodes.length > 1 ? "Model Codes" : "Model Code";

  return (
    <Modal
      width="70%"
      title="Service Schedule Master"
      visible={open}
      okText={editable ? "Save" : "Modify"}
      onCancel={close}
      okButtonProps={{ loading, disabled: !editable && !modify }}
      onOk={() => (editable ? handleSubmit() : setEditable(true))}
      wrapClassName="service-schedule-modal"
      destroyOnClose
    >
      <Form>
        <Row type="flex" justify="space-between">
          <Col span={10}>
            <Item label={codeLabel} colon={false} required>
              {getFieldDecorator("modelCodes", {
                rules: [{ required: true, message: "Select Model Code(s)" }],
              })(
                <Select
                  mode="multiple"
                  disabled={!editable}
                  placeholder="Select Model Code(s)"
                  style={{ width: "100%" }}
                  showAction={["click", "focus"]}
                  filterOption={(input, option) =>
                    option.props.children
                      .toString()
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={handleModelCodesChange}
                  tagRender={(tagProps) => {
                    const { label, closable, onClose } = tagProps;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          background: "#e6f7ff",
                          border: "1px solid #91d5ff",
                          color: "#096dd9",
                          borderRadius: "4px",
                          padding: "2px 8px",
                          margin: "2px 4px 2px 0",
                          fontWeight: 500,
                          fontSize: "12px",
                        }}
                      >
                        {label}
                        {closable && editable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: 6,
                              cursor: "pointer",
                              color: "#8c8c8c",
                              fontSize: "12px",
                              lineHeight: "1",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                >
                  {modelCodeOptions.map((item) => (
                    <Option key={item.modelCode} value={item.modelCode}>
                      {item.modelCode}
                    </Option>
                  ))}
                </Select>
              )}
            </Item>
          </Col>
          <Col span={10}>
            <Item label="No. of Services in Warranty" colon={false}>
              {getFieldDecorator("noOfServices", {
                rules: [{ required: false }],
              })(
                <Input
                  onInput={handleServiceCountChange}
                  disabled={!editable}
                  maxLength={3}
                  onWheel={(event) => event.currentTarget.blur()}
                  placeholder="No. of Services in Warranty"
                />
              )}
            </Item>
          </Col>
        </Row>

        <Row type="flex" justify="space-between">
          <Col span={10}>
            <Item label="Warranty Period in Days" colon={false}>
              {getFieldDecorator("warrantyDays", {
                rules: [{ required: false }],
              })(
                <Input
                  disabled={!editable}
                  placeholder="Warranty Period in Days"
                  onWheel={(event) => event.currentTarget.blur()}
                />
              )}
            </Item>
          </Col>
          <Col span={10}>
            <Item label="Service Interval Days After Warranty" colon={false}>
              {getFieldDecorator("serviceIntervalDays", {
                rules: [{ required: false }],
              })(
                <Input
                  disabled={!editable}
                  placeholder="Service Interval Days After Warranty"
                  onWheel={(event) => event.currentTarget.blur()}
                />
              )}
            </Item>
          </Col>
        </Row>

        <Row type="flex" justify="space-between">
          <Col span={10}>
            <Item label="Warranty Period in KMs" colon={false}>
              {getFieldDecorator("warrantyKm", {
                rules: [{ required: false }],
              })(
                <Input
                  disabled={!editable}
                  placeholder="Warranty Period in KMs"
                  onWheel={(event) => event.currentTarget.blur()}
                />
              )}
            </Item>
          </Col>
          <Col span={10}>
            <Item label="Service Interval KMs After Warranty" colon={false}>
              {getFieldDecorator("serviceIntervalKm", {
                rules: [{ required: false }],
              })(
                <Input
                  disabled={!editable}
                  placeholder="Service Interval KMs After Warranty"
                  onWheel={(event) => event.currentTarget.blur()}
                />
              )}
            </Item>
          </Col>
        </Row>

        <Row>
          <Col span={14}>
            <Item label="Service Table" colon={false}>
              <ServicesTable
                emitData={(d) => setServices(d)}
                editable={editable}
                dataSource={services}
                count={serviceCount >= 0 ? serviceCount : 0}
              />
            </Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Form.create({ name: "serviceSchedule" })(ServiceScheduleModal);
